import axios, { type AxiosError, type AxiosRequestConfig } from 'axios';

// 后端 swallow 框架统一响应包装体（ResponseEntity）
// status 为字符串："200"=成功，"500"=业务失败，"900"=系统异常
// code 为异常追踪码（UUID 字符串），仅异常时有值，不是状态码
export interface ResponseEntity<T = unknown> {
  st?: number;
  message?: string;
  status?: string;
  data?: T;
  errCode?: string;
  code?: string; // 异常追踪码 UUID，非状态码
  version?: string;
}

// 响应拦截器统一返回整个包装体（而非 AxiosResponse），
// 这里重写 axios 实例方法的返回类型以匹配实际运行时行为，支持泛型透传
declare module 'axios' {
  export interface AxiosInstance {
    <T = any>(config: AxiosRequestConfig): Promise<T>;
    <T = any>(url: string, config?: AxiosRequestConfig): Promise<T>;
    request<T = any>(config: AxiosRequestConfig): Promise<T>;
    get<T = any>(url: string, config?: AxiosRequestConfig): Promise<T>;
    delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<T>;
    head<T = any>(url: string, config?: AxiosRequestConfig): Promise<T>;
    options<T = any>(url: string, config?: AxiosRequestConfig): Promise<T>;
    post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T>;
    put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T>;
    patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T>;
  }
}

// 扩展 AxiosRequestConfig，支持重试计数与禁用重试标记
interface RetryableConfig extends AxiosRequestConfig {
  retryCount?: number;
  /** 显式禁用自动重试（生成类异步任务提交接口不可重试，避免重复创建任务） */
  noRetry?: boolean;
}

/**
 * 业务失败错误：在 message 之外保留 swallow 的 errCode 与 status，
 * 让调用方能按错误码分支处理（如画布乐观锁冲突 AI_IMAGE_CANVAS_CONFLICT），
 * 而不必去匹配中文文案。继承 Error，既有 instanceof / message 判断不受影响。
 */
export class ApiBizError extends Error {
  /** 后端业务错误码 */
  errCode?: string;
  /** swallow 业务状态："500" 业务失败 / "900" 系统异常 */
  status?: string;

  constructor(message: string, errCode?: string, status?: string) {
    super(message);
    this.name = 'ApiBizError';
    this.errCode = errCode;
    this.status = status;
  }
}

// 1. 创建 Axios 实例
const request = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL, // API 基础地址
  timeout: 1000*60*10, // 请求超时时间
  headers: {
    'Content-Type': 'application/json',
  },
});

// 手动实现请求重试机制（针对网络错误和5xx）
const MAX_RETRY = 2;
const RETRY_DELAY = 2000;

// 生成类提交接口白名单：显式关闭自动重试，
// 重试可能让后端重复创建生成任务（请求已受理但响应丢失时应由前端重新发起新任务，而非重放），
// 也会把一次已经打给上游模型的付费调用再烧一遍
const NO_RETRY_POST_URLS = [
  '/content-hub/generate',
  '/visual-studio/generate-image',
  '/visual-studio/generate-video-script',
  '/ai-image/generate',
  '/ai-image/enhance-prompt',
  '/digitalhuman/video/tts',
  '/digitalhuman/video/audio',
  '/digitalhuman/voice-clone',
  '/digitalhuman/figure',
];

function shouldRetry(error: AxiosError, config?: RetryableConfig): boolean {
  if (config?.noRetry) return false;
  const retryCount = config?.retryCount ?? 0;
  if (retryCount >= MAX_RETRY) return false;
  // 网络错误（无 response）
  if (!error.response) return true;
  // 5xx 服务端错误
  const status = error.response.status;
  return status >= 500 && status < 600;
}

// 2. 请求拦截器：统一处理 Token 和请求头
request.interceptors.request.use(
  (config) => {
    // 从本地存储获取 Token（使用 sessionStorage 减少 XSS 风险）
    const token = sessionStorage.getItem('token');

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // 动态添加其他通用请求头
    config.headers['x-admin-key'] = sessionStorage.getItem('opc_admin_key') ?? '';
    config.headers['X-Client-Version'] = '1.0.0';

    // 生成类 POST 接口按 URL 白名单显式禁用自动重试
    const reqUrl = config.url || '';
    if (
      config.method?.toLowerCase() === 'post' &&
      NO_RETRY_POST_URLS.some((u) => reqUrl === u || reqUrl.endsWith(u))
    ) {
      (config as RetryableConfig).noRetry = true;
    }

    return config;
  },
  (error: unknown) => {
    console.warn('[Request Interceptor Error]', error);
    return Promise.reject(error);
  }
);

// 3. 响应拦截器：统一处理全局错误提示
let isRedirecting = false;

/** 登录超时 / 认证失效时统一处理：清除 token 并跳转登录页 */
function handleAuthTimeout() {
  if (isRedirecting) return;
  isRedirecting = true;
  sessionStorage.removeItem('token');
  window.dispatchEvent(new CustomEvent('auth:unauthorized'));
  setTimeout(() => { isRedirecting = false; }, 3000);
}

request.interceptors.response.use(
  (response) => {
    console.log(response)
    // 后端由 swallow 框架统一包装响应：{ st, message, status: "200"|"500"|"900"|"1099", data, errCode, code, version }
    const resData = response.data as ResponseEntity;
    const bizStatus = String(resData.status ?? '');
    const errCode = resData.errCode;

    if (bizStatus === '200') {
      // 业务请求成功，保持返回整个包装体（页面通过 res?.data ?? res 方式消费）
      return response.data;
    } else {
      // 业务状态 1099：登录超时 / Token 过期
      if (bizStatus === '1099' || bizStatus === '500'&&errCode==='401') {
        console.warn('[Auth] 业务层登录超时，清除登录状态');
        handleAuthTimeout();
        return Promise.reject(new Error('登录已超时，请重新登录'));
      }
      // 业务失败（"500"）或系统异常（"900"），错误信息取 message
      const message =
        resData.message || (bizStatus === '900' ? '系统繁忙，请稍后重试' : '请求失败');
      console.warn(`[API Business Error] ${message}`);
      return Promise.reject(new ApiBizError(message, errCode, bizStatus));
    }
  },
  async (error: unknown) => {
    const axiosError = error as AxiosError;
    
    // 重试机制：网络错误和5xx自动重试
    if (shouldRetry(axiosError, axiosError.config as RetryableConfig)) {
      const config = axiosError.config as RetryableConfig;
      if (config) {
        config.retryCount = (config.retryCount ?? 0) + 1;
        await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY * config.retryCount!));
        return request(config);
      }
    }

    // HTTP 状态码错误处理
    if (axiosError.response) {
      const { status } = axiosError.response;
     
      switch (status) {
        case 401:
          console.warn('[Auth] 登录已过期，请重新登录');
          break;
        case 403:
          console.warn('[Auth] 没有权限执行此操作');
          break;
        case 404:
          console.warn('[API] 请求的资源不存在');
          break;
        case 500:
          console.warn('[Server] 服务器内部错误，请稍后再试');
          break;
        default:
          console.warn(`[API] 请求错误: ${status}`);
      }
    } else if (axiosError.request) {
      console.warn('[Network] 网络错误，请检查网络连接');
    } else {
      console.warn('[Request]', (error instanceof Error ? error.message : '请求配置错误'));
    }
    return Promise.reject(error);
  }
);

export default request;
